"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createApolloAuthenticationLink = exports.initApolloParameters = void 0;
const core_1 = require("@apollo/client/core");
const error_1 = require("@apollo/client/link/error");
const retry_1 = require("@apollo/client/link/retry");
const api_1 = require("../../api");
const authentication_1 = require("../authentication");
const storageManagement_1 = require("../tokens-storage/storageManagement");
const adaptStorage_1 = require("../tokens-storage/adaptStorage");
// 4 minutes
const TOKEN_TTL = 240;
let storeDispatch;
const initApolloParameters = ({ dispatch }) => {
    if (dispatch) {
        storeDispatch = dispatch;
    }
};
exports.initApolloParameters = initApolloParameters;
async function getCurrentAccessToken(storage) {
    return (0, storageManagement_1.getAccessToken)(storage);
}
async function setAccessToken(storage, newAccessToken) {
    storeDispatch?.((0, authentication_1.stytchSetAuthenticated)(!!newAccessToken));
    await (0, storageManagement_1.setAccessToken)(storage, newAccessToken);
    await (0, storageManagement_1.setOldAccessToken)(storage, newAccessToken);
    return newAccessToken;
}
async function updateAccessToken(storage, userServiceAddress) {
    const oldAccessToken = await getCurrentAccessToken(storage);
    if (oldAccessToken) {
        try {
            const response = await (0, api_1.stytchFetchRefreshToken)(oldAccessToken, userServiceAddress);
            const responseJson = await response.json();
            const newAccessToken = responseJson.session_jwt;
            await setAccessToken(storage, newAccessToken);
        }
        catch (error) {
            signOut();
        }
    }
}
async function getCurrentAccessTokenTimestamp(storage) {
    return (0, storageManagement_1.getAccessTokenTimestamp)(storage);
}
function signOut() {
    storeDispatch?.((0, authentication_1.stytchSignOut)());
}
function processAuthenticationError(storage, userServiceAddress, operation, forward) {
    return (0, core_1.fromPromise)(updateAccessToken(storage, userServiceAddress)
        .then(() => getCurrentAccessToken(storage))
        .then((accessToken) => {
        const oldHeaders = operation.getContext().headers;
        operation.setContext({
            headers: {
                ...oldHeaders,
                authorization: `Bearer ${accessToken || ''}`,
            },
        });
        return forward(operation);
    })
        .catch(() => {
        signOut();
    })).flatMap(() => {
        return forward(operation);
    });
}
function createApolloAuthenticationLink(uri, _storage, userServiceAddress) {
    const storage = (0, adaptStorage_1.adaptStorage)(_storage);
    const httpLink = new core_1.HttpLink({
        uri,
        credentials: 'same-origin',
    });
    const authLink = new core_1.ApolloLink((operation, forward) => {
        return (0, core_1.fromPromise)(getCurrentAccessTokenTimestamp(storage).then((timestamp) => {
            if (timestamp) {
                const now = new Date();
                const nowTimestamp = Math.floor(now.getTime() / 1000);
                const delta = nowTimestamp - timestamp;
                if (delta > TOKEN_TTL) {
                    // refresh token
                    return processAuthenticationError(storage, userServiceAddress, operation, forward);
                }
                else {
                    // normal flow
                    return getCurrentAccessToken(storage).then((accessToken) => {
                        if (accessToken) {
                            operation.setContext(({ headers = {} }) => ({
                                headers: {
                                    ...headers,
                                    authorization: `Bearer ${accessToken}`,
                                },
                            }));
                        }
                        return forward(operation);
                    });
                }
            }
            return forward(operation);
        })).flatMap(() => {
            return forward(operation);
        });
    });
    // will run on Graphql error and network error
    const errorLink = (0, error_1.onError)(({ graphQLErrors, operation, forward }) => {
        if (graphQLErrors) {
            for (const err of graphQLErrors) {
                if (err.message === 'Authentication required') {
                    return processAuthenticationError(storage, userServiceAddress, operation, forward);
                }
            }
        }
    });
    // will run retry on Network error
    const retryLink = new retry_1.RetryLink();
    // combined link
    const link = (0, core_1.from)([errorLink, retryLink, authLink, httpLink]);
    // return the link
    return link;
}
exports.createApolloAuthenticationLink = createApolloAuthenticationLink;
